import React, { useState, useEffect } from 'react';
import { FaUserMd, FaEdit, FaSave, FaUserInjured, FaLock } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { updateProfile, changePassword, uploadProfileImage, getFileUrl, getDoctorHolidays } from '../services/api';

const ALL_TIME_SLOTS = [
  '08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30',
  '12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30',
  '16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30',
];

const Profile = () => {
  const { showToast, user, refreshUser } = useApp();
  const role = user?.role || 'Doctor';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = React.useRef(null);
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [doctorHolidays, setDoctorHolidays] = useState([]);
  const [profile, setProfile] = useState({
    name: '', specialization: '', regNo: '', phone: '', email: '',
    address: '', experience: '', degree: '', hospital: '', about: '',
    timing: '', consultFee: '', age: '', gender: '', bloodGroup: '',
    medicalHistory: '', allergies: '', profileImage: '',
    onlineSlots: [], offlineSlots: [], slotCapacity: 1,
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        specialization: user.specialization || '',
        regNo: user.regNo || '',
        phone: user.phone || '',
        email: user.email || '',
        address: user.address || '',
        experience: user.experience || '',
        degree: user.degree || '',
        hospital: user.hospital || '',
        about: user.about || '',
        timing: user.timing || '',
        consultFee: user.consultFee || '',
        age: user.age || '',
        gender: user.gender || '',
        bloodGroup: user.bloodGroup || '',
        medicalHistory: user.medicalHistory || '',
        allergies: user.allergies || '',
        profileImage: user.profileImage || '',
        onlineSlots:  Array.isArray(user.onlineSlots)  ? user.onlineSlots  : [],
        offlineSlots: Array.isArray(user.offlineSlots) ? user.offlineSlots : [],
        slotCapacity: user.slotCapacity || 1,
      });
    }
    if (user?.role === 'Doctor' && user?.id) {
      getDoctorHolidays(user.id).then(r => setDoctorHolidays(r.data?.data || [])).catch(() => setDoctorHolidays([]));
    }
  }, [user]);

  // Annual holidays recur every year (no year shown); one-time holidays show
  // the exact year they block. Backend already sorts by date ascending.
  const formatHolidayDate = (h) => {
    const [y, m, d] = h.date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const day = dt.getUTCDate();
    const mon = dt.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    return h.type === 'annual' ? `${day} ${mon}` : `${day} ${mon} ${y}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(profile);
      await refreshUser();
      setEditing(false);
      showToast('Profile updated!', 'success');
    } catch (err) {
      showToast(err.message || 'Update failed', 'error');
    }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPassword !== pwForm.confirm) return showToast('Passwords do not match', 'error');
    if (pwForm.newPassword.length < 6) return showToast('Password must be at least 6 characters', 'error');
    try {
      await changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      showToast('Password changed!', 'success');
      setShowPwModal(false);
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { showToast(err.message || 'Failed', 'error'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('profileImage', file);
    try {
      const res = await uploadProfileImage(formData);
      setProfile(p => ({ ...p, profileImage: res.data.url }));
      await refreshUser();
      showToast('Profile image updated!', 'success');
    } catch (err) {
      showToast(err.message || 'Image upload failed', 'error');
    }
    setUploadingImage(false);
  };

  const initials = user?.name ? user.name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';
  const roleLabel = role === 'Doctor' ? `Dr. ${profile.name}` : profile.name;

  const renderField = (field, label, type = 'text', options = []) => (
    <div className="profile-info-row" key={field}>
      <div style={{ flex: 1 }}>
        <div className="profile-info-label">{label}</div>
        {editing ? (
          type === 'textarea' ? (
            <textarea value={profile[field]} onChange={e => setProfile(p => ({ ...p, [field]: e.target.value }))} className="profile-edit-input" style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 8, border: '2px solid var(--primary)', minHeight: 80, fontFamily: 'inherit' }} />
          ) : type === 'select' ? (
            <select value={profile[field]} onChange={e => setProfile(p => ({ ...p, [field]: e.target.value }))} className="profile-edit-input" style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 8, border: '2px solid var(--primary)', fontFamily: 'inherit', background: 'white' }}>
              <option value="">Select...</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type={type} value={profile[field]} onChange={e => setProfile(p => ({ ...p, [field]: e.target.value }))} className="profile-edit-input" />
          )
        ) : <div className="profile-info-val">{profile[field] || <span style={{ color: 'var(--text-light)' }}>Not set</span>}</div>}
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <Header title="Profile" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div><h1>{role === 'Doctor' ? <FaUserMd style={{ color: 'var(--primary)', marginRight: 8 }} /> : <FaUserInjured style={{ color: 'var(--primary)', marginRight: 8 }} />}My Profile</h1></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={() => setShowPwModal(true)}>
              <FaLock /> Password
            </button>
            <button className="btn btn-outline" onClick={() => editing ? handleSave() : setEditing(true)} disabled={saving}>
              {editing ? (saving ? 'Saving...' : <><FaSave /> Save</>) : <><FaEdit /> Edit</>}
            </button>
          </div>
        </div>

        <div className="profile-header-card card">
          <div 
            className="profile-avatar" 
            style={{ cursor: 'pointer', position: 'relative', overflow: 'hidden' }}
            onClick={() => imageInputRef.current?.click()}
            title="Click to update profile image"
          >
            {uploadingImage ? (
              <span style={{ fontSize: 14 }}>...</span>
            ) : profile.profileImage ? (
              <img src={getFileUrl(profile.profileImage)} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            ) : initials}
            <div className="avatar-overlay">Upload</div>
          </div>
          <input type="file" accept="image/*" ref={imageInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />
          <div className="profile-info">
            {editing ? (
              <input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="profile-edit-input name" placeholder="Full name" />
            ) : <div className="profile-name">{roleLabel}</div>}
            {role === 'Doctor' && (
              editing ? (
                <input value={profile.specialization} onChange={e => setProfile(p => ({ ...p, specialization: e.target.value }))} className="profile-edit-input spec" placeholder="Specialization" />
              ) : <div className="profile-spec">{profile.specialization || 'Specialization not set'}</div>
            )}
            <div className="profile-reg">{user?.email}</div>
            <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: role === 'Admin' ? '#ede9fe' : role === 'Doctor' ? '#e3f2fd' : '#e8f5e9', color: role === 'Admin' ? '#7c3aed' : role === 'Doctor' ? '#1976d2' : '#2e7d32' }}>
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </span>
          </div>
        </div>

        <div className="card profile-section">
          <div className="section-title">Personal Information</div>
          {renderField('phone', 'Phone')}
          {renderField('email', 'Email (Cannot be changed)', 'text')}
          {renderField('address', 'Address', 'textarea')}
          {renderField('gender', 'Gender', 'select', ['Male', 'Female', 'Other'])}
          {renderField('age', 'Age', 'number')}
        </div>

        {role === 'Doctor' && (
          <div className="card profile-section">
            <div className="section-title">Professional Details</div>
            {renderField('degree', 'Qualification')}
            {renderField('hospital', 'Hospital / Clinic')}
            {renderField('experience', 'Experience')}
            {renderField('consultFee', 'Consultation Fee (₹)')}
            {renderField('timing', 'Working Hours')}
            {renderField('regNo', 'Registration No.')}
          </div>
        )}

        {role === 'Doctor' && (
          <div className="card profile-section">
            <div className="section-title">Appointment Slots</div>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>
              Select times you are available. Patients see only these slots when booking.
            </p>
            <div className="profile-info-row" style={{ marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <div className="profile-info-label">Max Patients Per Slot</div>
                {editing ? (
                  <input type="number" min="1" max="50" value={profile.slotCapacity} onChange={e => setProfile(p => ({ ...p, slotCapacity: parseInt(e.target.value) || 1 }))} className="profile-edit-input" style={{ width: 100 }} />
                ) : (
                  <div className="profile-info-val">{profile.slotCapacity || 1} patient{(profile.slotCapacity || 1) > 1 ? 's' : ''} per slot</div>
                )}
              </div>
            </div>
            {['Online', 'Offline'].map(mode => {
              const key = mode === 'Online' ? 'onlineSlots' : 'offlineSlots';
              const selected = profile[key] || [];
              const toggleSlot = (t) => {
                if (!editing) return;
                setProfile(p => ({
                  ...p,
                  [key]: selected.includes(t) ? selected.filter(s => s !== t) : [...selected, t].sort(),
                }));
              };
              return (
                <div key={mode} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-medium)', marginBottom: 10 }}>
                    {mode === 'Online' ? '🌐 Online Slots' : '🏥 Offline Slots'}
                    {selected.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--primary)', background: 'var(--primary-bg)', padding: '2px 8px', borderRadius: 10 }}>{selected.length} selected</span>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                    {ALL_TIME_SLOTS.map(t => (
                      <div
                        key={t}
                        onClick={() => toggleSlot(t)}
                        style={{
                          padding: '7px 4px',
                          borderRadius: 8,
                          border: `2px solid ${selected.includes(t) ? 'var(--primary)' : 'var(--border)'}`,
                          background: selected.includes(t) ? 'var(--primary)' : editing ? 'white' : 'var(--primary-bg)',
                          color: selected.includes(t) ? 'white' : 'var(--text-medium)',
                          fontSize: 11,
                          fontWeight: 600,
                          textAlign: 'center',
                          cursor: editing ? 'pointer' : 'default',
                          opacity: editing ? 1 : 0.85,
                          transition: 'all 0.15s',
                        }}
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {role === 'Doctor' && (
          <div className="card profile-section">
            <div className="section-title">Availability</div>
            <div style={{ marginBottom: 16 }}>
              <div className="profile-info-label">Non-working Days</div>
              <div className="profile-info-val">{user?.worksWeekends ? 'None — available all days' : 'Saturday, Sunday'}</div>
            </div>
            <div>
              <div className="profile-info-label" style={{ marginBottom: 8 }}>Doctor Holidays</div>
              {doctorHolidays.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>No holidays configured. Contact admin to add one.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {doctorHolidays.map(h => (
                    <div key={h.id} style={{ fontSize: 13, color: h.status === 'active' ? 'var(--text-dark)' : 'var(--text-light)', display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, minWidth: 70 }}>{formatHolidayDate(h)}</span>
                      <span>— {h.reason || 'Holiday'} —</span>
                      <span style={{ fontStyle: 'italic' }}>{h.type === 'annual' ? 'Every Year' : 'One-time'}</span>
                      {h.status !== 'active' && <span style={{ fontSize: 11, background: '#f0e8e4', padding: '1px 6px', borderRadius: 6 }}>Disabled</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {(role === 'Patient' || role === 'Admin') && (
          <div className="card profile-section">
            <div className="section-title">Health Information</div>
            {renderField('bloodGroup', 'Blood Group', 'select', ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'])}
            {renderField('medicalHistory', 'Medical History', 'textarea')}
            {renderField('allergies', 'Allergies', 'textarea')}
          </div>
        )}

        {(role === 'Patient' || role === 'Doctor' || role === 'Admin') && (
          <div className="card profile-section">
            <div className="section-title">Additional Details</div>
            {role === 'Patient' && renderField('hospital', 'Preferred Hospital')}
            {renderField('about', 'About', 'textarea')}
          </div>
        )}
      </main>
      <BottomNav />

      {showPwModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPwModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2><FaLock /> Change Password</h2>
              <button className="modal-close" onClick={() => setShowPwModal(false)}>×</button>
            </div>
            {['currentPassword', 'newPassword', 'confirm'].map(f => (
              <div className="form-group" key={f}>
                <label>{f === 'currentPassword' ? 'Current Password' : f === 'newPassword' ? 'New Password' : 'Confirm New Password'}</label>
                <input type="password" value={pwForm[f]} onChange={e => setPwForm(p => ({ ...p, [f]: e.target.value }))} placeholder="••••••••" />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowPwModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handlePasswordChange}>Update Password</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .profile-header-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; margin-bottom: 12px; }
        .profile-avatar { width: 70px; height: 70px; background: linear-gradient(135deg,#E8500A,#FF6B2C); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 26px; font-weight: 800; flex-shrink: 0; box-shadow: 0 4px 16px rgba(232,80,10,0.35); position: relative; overflow: hidden; cursor: pointer; }
        .avatar-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.5); color: white; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s; }
        .profile-avatar:hover .avatar-overlay { opacity: 1; }
        .profile-info { flex: 1; }
        .profile-name { font-size: 18px; font-weight: 800; color: var(--text-dark); }
        .profile-spec { font-size: 13px; color: var(--text-medium); margin-top: 2px; }
        .profile-reg { font-size: 12px; color: var(--primary); font-weight: 600; margin-top: 4px; }
        .profile-section { padding: 16px; margin-bottom: 12px; }
        .profile-section .section-title { margin-bottom: 14px !important; }
        .profile-info-row { display: flex; align-items: flex-start; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--border); }
        .profile-info-row:last-child { border-bottom: none; }
        .profile-info-label { font-size: 11px; color: var(--text-light); font-weight: 600; text-transform: uppercase; margin-bottom: 2px; }
        .profile-info-val { font-size: 14px; color: var(--text-dark); font-weight: 500; }
        .profile-edit-input { font-size: 14px; color: var(--text-dark); border: 2px solid var(--primary); border-radius: 8px; padding: 6px 10px; width: 100%; font-family: inherit; }
        .profile-edit-input.name { font-size: 17px; font-weight: 700; }
        .profile-edit-input.spec { font-size: 13px; margin-top: 4px; }
      `}</style>
    </div>
  );
};

export default Profile;
